import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Briefcase, Globe, Instagram, Twitter, Linkedin } from 'lucide-react';
import { portalApi } from '../../contexts/PortalAuthContext';

export function PortalCohort() {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    portalApi.get('/cohort').then(({ data }) => setMembers(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="portal-cohort">
      <header className="mb-8">
        <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Members</div>
        <h1 className="text-3xl font-semibold text-white">Members</h1>
        <p className="text-slate-400 mt-2">Meet who you&apos;re learning alongside. Click a profile to view bio &amp; links — or jump to <Link to="/portal/community" className="text-amber-400 hover:underline">Community</Link> to start the conversation.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map(m => (
          <Link
            key={m.id}
            to={`/portal/cohort/${m.id}`}
            className="block rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 p-5 transition"
            data-testid={`cohort-member-${m.id}`}
          >
            <div className="flex items-start gap-4">
              {m.avatar_url ? (
                <img
                  src={m.avatar_url.startsWith('http') ? m.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${m.avatar_url}`}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover border border-slate-700"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center text-sm font-semibold text-slate-400 border border-slate-700">
                  {(m.full_name || 'U').slice(0,1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-white font-medium truncate">{m.full_name}</div>
                  {m.role === 'admin' && <span className="text-[10px] uppercase bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Mentor</span>}
                </div>
                {m.business && <div className="text-xs text-slate-400 truncate mt-0.5">{m.business}</div>}
                {m.location && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.location}</div>}
              </div>
            </div>
            {m.bio && <p className="text-sm text-slate-400 mt-3 line-clamp-2">{m.bio}</p>}
          </Link>
        ))}
        {members.length === 0 && <div className="text-slate-500 text-sm col-span-3">No members yet.</div>}
      </div>
    </div>
  );
}

export function PortalCohortMember() {
  const { userId } = useParams();
  const [m, setM] = useState(null);
  useEffect(() => {
    portalApi.get(`/cohort/${userId}`).then(({ data }) => setM(data)).catch(() => {});
  }, [userId]);

  if (!m) return <div className="text-slate-400">Loading…</div>;

  const sl = m.social_links || {};
  const avatarSrc = m.avatar_url
    ? (m.avatar_url.startsWith('http') ? m.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${m.avatar_url}`)
    : null;

  return (
    <div data-testid="portal-cohort-detail">
      <Link to="/portal/cohort" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-2 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to cohort
      </Link>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-28 h-28 rounded-full object-cover border-2 border-slate-700" />
          ) : (
            <div className="w-28 h-28 rounded-full bg-slate-800 flex items-center justify-center text-3xl font-semibold text-slate-400 border-2 border-slate-700">
              {(m.full_name || 'U').slice(0,1).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">{m.full_name}</h1>
            {m.role === 'admin' && <span className="text-xs uppercase bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded ml-1">Mentor</span>}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-sm text-slate-400">
              {m.business && <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {m.business}</span>}
              {m.location && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {m.location}</span>}
            </div>
            {m.bio && <p className="text-slate-300 mt-4 whitespace-pre-line">{m.bio}</p>}

            <div className="flex flex-wrap gap-3 mt-5">
              {sl.website && <Social icon={Globe} href={sl.website} label="Website" />}
              {sl.instagram && <Social icon={Instagram} href={sl.instagram} label="Instagram" />}
              {sl.twitter && <Social icon={Twitter} href={sl.twitter} label="X" />}
              {sl.linkedin && <Social icon={Linkedin} href={sl.linkedin} label="LinkedIn" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Social({ icon: Icon, href, label }) {
  const url = href.startsWith('http') ? href : `https://${href.replace('@', '')}`;
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-full"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </a>
  );
}
